import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X, GripHorizontal } from "lucide-react";

type Snap = "half" | "full";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  /** Initial snap point when opening. Default "half". */
  initialSnap?: Snap;
}

/**
 * Mobile-only bottom sheet with drag-to-resize and swipe-to-dismiss.
 * - Drag the handle: snaps to half (~55vh) or full (~92vh).
 * - Drag below half: closes.
 * - Backdrop tap or Esc: closes.
 * - Locks body scroll while open.
 */
export function MobileMapSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  initialSnap = "half",
}: Props) {
  const [mounted, setMounted] = useState(false);
  const [snap, setSnap] = useState<Snap>(initialSnap);
  const [drag, setDrag] = useState<number>(0); // px offset from current snap; positive = down
  const startY = useRef<number | null>(null);
  const activePointer = useRef<number | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  // Reset snap + drag when reopening
  useEffect(() => {
    if (open) {
      setSnap(initialSnap);
      setDrag(0);
    }
  }, [open, initialSnap]);

  // Body scroll lock + Esc to close
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const snapHeights = useMemo(
    () => ({
      half: "55vh",
      full: "92vh",
    }),
    [],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    activePointer.current = e.pointerId;
    startY.current = e.clientY;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (activePointer.current !== e.pointerId || startY.current == null) return;
    const dy = e.clientY - startY.current;
    // Allow dragging both directions but clamp upward movement so full snap
    // doesn't drag above the top.
    const clamped = snap === "full" ? Math.max(-40, dy) : dy;
    setDrag(clamped);
  };

  const settle = (dy: number) => {
    const vh = window.innerHeight;
    // Positive dy = user dragged down.
    if (snap === "full") {
      if (dy > vh * 0.25) setSnap("half");
      else if (dy > vh * 0.55) onClose();
    } else {
      if (dy < -vh * 0.1) setSnap("full");
      else if (dy > vh * 0.18) onClose();
    }
    setDrag(0);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (activePointer.current !== e.pointerId) return;
    const dy = startY.current != null ? e.clientY - startY.current : 0;
    activePointer.current = null;
    startY.current = null;
    settle(dy);
  };

  if (!mounted) return null;

  const sheetTransform = open
    ? `translate3d(0, ${drag}px, 0)`
    : "translate3d(0, 100%, 0)";

  return createPortal(
    <div
      className="fixed inset-0 z-[80]"
      aria-hidden={!open}
      role="dialog"
      aria-modal="true"
      aria-label={title ?? "Map"}
      style={{ pointerEvents: open ? "auto" : "none" }}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close map"
        onClick={onClose}
        className={`absolute inset-0 bg-navy/50 backdrop-blur-[2px] transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="absolute inset-x-0 bottom-0 flex flex-col rounded-t-3xl bg-white shadow-[0_-12px_40px_-8px_rgba(15,23,42,0.35)] will-change-transform"
        style={{
          height: snapHeights[snap],
          transform: sheetTransform,
          transition:
            activePointer.current == null
              ? "transform 260ms cubic-bezier(0.22, 1, 0.36, 1), height 260ms cubic-bezier(0.22, 1, 0.36, 1)"
              : "none",
          paddingBottom: "env(safe-area-inset-bottom)",
          touchAction: "none",
        }}
      >
        {/* Drag handle */}
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="flex flex-col items-center pt-2.5 pb-1.5 cursor-grab active:cursor-grabbing select-none"
          aria-label="Drag to resize"
          role="button"
        >
          <span className="block h-1.5 w-11 rounded-full bg-navy/25" />
          <GripHorizontal className="sr-only" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 pb-2">
          <div className="min-w-0">
            {title && (
              <h2 className="font-heading text-navy text-base leading-tight truncate">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-[11px] uppercase tracking-widest font-mono text-navy/55 mt-0.5 truncate">
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 -mt-1 -mr-1 inline-flex items-center justify-center size-9 rounded-full text-navy/70 hover:bg-navy/5 active:scale-95"
            aria-label="Close"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain px-4 pb-6"
          style={{ touchAction: "pan-y" }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
